import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import { useGeolocation, GeolocationCoords } from '../../../hooks/useGeolocation';
import { cepToCoords, cityToCoords } from '../../../utils/geoHelpers';
import CepMask from '../../../components/InputMask/CepMask';

interface Props {
  onLocationResolved: (coords: GeolocationCoords) => void;
}

type FallbackTab = 'cep' | 'city';

function LocationGate({ onLocationResolved }: Props) {
  const { coords, loading } = useGeolocation();

  const [resolved, setResolved] = useState(false);
  const [activeTab, setActiveTab] = useState<FallbackTab>('cep');
  const [cepValue, setCepValue] = useState('');
  const [cityValue, setCityValue] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const handleResolved = useCallback(
    (resolvedCoords: GeolocationCoords) => {
      setResolved(true);
      onLocationResolved(resolvedCoords);
    },
    [onLocationResolved],
  );

  useEffect(() => {
    if (coords && !resolved) handleResolved(coords);
  }, [coords, resolved, handleResolved]);

  if (resolved) return null;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 12,
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: '#00b4e4' }} size={40} />
        <Typography color="text.secondary">Detectando sua localização...</Typography>
      </Box>
    );
  }

  const handleTabChange = (_: React.SyntheticEvent, value: FallbackTab) => {
    setActiveTab(value);
    setResolveError(null);
  };

  const handleCepSubmit = async () => {
    setResolveError(null);
    setResolving(true);
    try {
      const resolvedCoords = await cepToCoords(cepValue);
      handleResolved(resolvedCoords);
    } catch (err) {
      setResolveError(err instanceof Error ? err.message : 'CEP inválido ou não encontrado');
    } finally {
      setResolving(false);
    }
  };

  const handleCitySubmit = async () => {
    setResolveError(null);
    if (!cityValue.trim()) return;
    setResolving(true);
    try {
      const resolvedCoords = await cityToCoords(cityValue);
      handleResolved(resolvedCoords);
    } catch (err) {
      setResolveError(err instanceof Error ? err.message : 'Cidade não encontrada');
    } finally {
      setResolving(false);
    }
  };

  const isCepReady = cepValue.replace(/\D/g, '').length === 8;
  const isCityReady = cityValue.trim().length > 0;
  const canSubmit = activeTab === 'cep' ? isCepReady : isCityReady;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: { xs: 4, md: 8 },
        px: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 480,
          width: '100%',
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
        }}
      >
        <Stack spacing={3} alignItems="center">
          <Box
            sx={{
              bgcolor: '#e0f7ff',
              borderRadius: '50%',
              p: 2,
              display: 'flex',
            }}
          >
            <LocationOnIcon sx={{ color: '#00b4e4', fontSize: 36 }} />
          </Box>

          <Stack spacing={1} alignItems="center" textAlign="center">
            <Typography variant="h6" fontWeight={700}>
              Onde você está?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Informe sua localização para ver os serviços disponíveis na sua região.
            </Typography>
          </Stack>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              width: '100%',
              '& .MuiTab-root.Mui-selected': { color: '#00b4e4' },
              '& .MuiTabs-indicator': { bgcolor: '#00b4e4' },
            }}
          >
            <Tab
              label="Buscar por CEP"
              value="cep"
              sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              label="Buscar por cidade"
              value="city"
              sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}
            />
          </Tabs>

          {activeTab === 'cep' && (
            <Stack spacing={1} width="100%">
              <InputLabel htmlFor="location-cep-input">CEP</InputLabel>
              <OutlinedInput
                id="location-cep-input"
                name="cep"
                placeholder="00000-000"
                value={cepValue}
                onChange={(e) => setCepValue(e.target.value)}
                inputComponent={CepMask as any}
                fullWidth
                onKeyDown={(e) => e.key === 'Enter' && isCepReady && handleCepSubmit()}
              />
            </Stack>
          )}

          {activeTab === 'city' && (
            <Stack spacing={1} width="100%">
              <InputLabel htmlFor="location-city-input">Cidade</InputLabel>
              <OutlinedInput
                id="location-city-input"
                placeholder="Ex: São Paulo, SP"
                value={cityValue}
                onChange={(e) => setCityValue(e.target.value)}
                fullWidth
                onKeyDown={(e) => e.key === 'Enter' && isCityReady && handleCitySubmit()}
              />
            </Stack>
          )}

          {resolveError && (
            <FormHelperText sx={{ color: 'error.main', textAlign: 'center', m: 0 }}>
              {resolveError}
            </FormHelperText>
          )}

          <Button
            variant="contained"
            fullWidth
            startIcon={
              resolving ? (
                <CircularProgress size={16} sx={{ color: '#fff' }} />
              ) : (
                <SearchIcon />
              )
            }
            disabled={resolving || !canSubmit}
            onClick={activeTab === 'cep' ? handleCepSubmit : handleCitySubmit}
            sx={{
              bgcolor: '#00b4e4',
              '&:hover': { bgcolor: '#0099c6' },
              borderRadius: 6,
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
            }}
          >
            {resolving ? 'Buscando...' : 'Buscar serviços'}
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}

export default LocationGate;
